/**
 * Parse/serialize workspace sidebar filters to URL query params.
 * Keys align with the collections API (fetchFileList / sigcap/api/views).
 */

export const WORKSPACE_FILTER_PARAM_KEYS = [
  'start_date',
  'end_date',
  'timezone',
  'northwest',
  'southeast',
  'geopolygon',
  'band_name',
  'tag_name',
  'campaign_name',
] as const;

export type WorkspaceFilterParamKey = (typeof WORKSPACE_FILTER_PARAM_KEYS)[number];

export interface WorkspaceAppliedFilters {
  confirmedStartDate: Date | null;
  confirmedEndDate: Date | null;
  timezoneOffset: string;
  northWest: string;
  southEast: string;
  geoPolygon: number[][] | undefined;
  bandName: string[];
  tagName: string[];
  campaignName: string[];
}

export type ParsedWorkspaceFilters = Partial<{
  confirmedStartDate: Date;
  confirmedEndDate: Date;
  timezoneOffset: string;
  northWest: string;
  southEast: string;
  geoPolygon: number[][];
  bandName: string[];
  tagName: string[];
  campaignName: string[];
}>;

function parseIsoDate(value: string): Date | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    console.warn(`workspaceFilterParams: invalid date "${value}"`);
    return null;
  }
  return date;
}

function parseCommaSeparatedList(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Normalize GeoJSON ring `[[lng, lat], ...]` or Polygon `[[[lng, lat], ...]]`. */
export function normalizeGeopolygonRing(parsed: unknown): number[][] | null {
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return null;
  }
  const first = parsed[0];
  if (
    Array.isArray(first) &&
    Array.isArray(first[0]) &&
    typeof (first[0] as number[])[0] === 'number'
  ) {
    return normalizeGeopolygonRing(first);
  }
  for (const point of parsed) {
    if (
      !Array.isArray(point) ||
      point.length < 2 ||
      typeof point[0] !== 'number' ||
      typeof point[1] !== 'number'
    ) {
      return null;
    }
  }
  return parsed as number[][];
}

/** Parse a geopolygon JSON string (ring or GeoJSON Polygon) from UI state. */
export function parseGeoPolygonString(value: string): number[][] | null {
  if (!value.trim()) {
    return null;
  }
  try {
    return normalizeGeopolygonRing(JSON.parse(value));
  } catch {
    return null;
  }
}

function parseGeopolygon(value: string): number[][] | null {
  try {
    const parsed = JSON.parse(value);
    const ring = normalizeGeopolygonRing(parsed);
    if (!ring) {
      console.warn('workspaceFilterParams: geopolygon must be a non-empty array');
      return null;
    }
    return ring;
  } catch {
    console.warn('workspaceFilterParams: failed to parse geopolygon JSON');
    return null;
  }
}

export function parseWorkspaceFilters(
  searchParams: URLSearchParams,
  options?: { includeAuthOnlyFilters?: boolean },
): ParsedWorkspaceFilters {
  const includeAuthOnly = options?.includeAuthOnlyFilters !== false;
  const result: ParsedWorkspaceFilters = {};

  const startDateRaw = searchParams.get('start_date');
  if (startDateRaw) {
    const d = parseIsoDate(startDateRaw);
    if (d) result.confirmedStartDate = d;
  }

  const endDateRaw = searchParams.get('end_date');
  if (endDateRaw) {
    const d = parseIsoDate(endDateRaw);
    if (d) result.confirmedEndDate = d;
  }

  const timezone = searchParams.get('timezone');
  if (timezone) {
    result.timezoneOffset = timezone;
  }

  const northwest = searchParams.get('northwest');
  if (northwest) {
    result.northWest = northwest;
  }

  const southeast = searchParams.get('southeast');
  if (southeast) {
    result.southEast = southeast;
  }

  const geopolygonRaw = searchParams.get('geopolygon');
  if (geopolygonRaw) {
    const polygon = parseGeopolygon(geopolygonRaw);
    if (polygon) result.geoPolygon = polygon;
  }

  const bandName = searchParams.get('band_name');
  if (bandName) {
    const bands = parseCommaSeparatedList(bandName);
    if (bands.length > 0) result.bandName = bands;
  }

  if (includeAuthOnly) {
    const tagName = searchParams.get('tag_name');
    if (tagName) {
      const tags = parseCommaSeparatedList(tagName);
      if (tags.length > 0) result.tagName = tags;
    }

    const campaignName = searchParams.get('campaign_name');
    if (campaignName) {
      const campaigns = parseCommaSeparatedList(campaignName);
      if (campaigns.length > 0) result.campaignName = campaigns;
    }
  }

  return result;
}

export function buildWorkspaceFilterSearchParams(
  filters: WorkspaceAppliedFilters,
  options?: { includeAuthOnlyFilters?: boolean },
): URLSearchParams {
  const includeAuthOnly = options?.includeAuthOnlyFilters !== false;
  const params = new URLSearchParams();

  if (filters.confirmedStartDate) {
    params.set('start_date', filters.confirmedStartDate.toISOString());
  }
  if (filters.confirmedEndDate) {
    params.set('end_date', filters.confirmedEndDate.toISOString());
  }
  const hasDates =
    filters.confirmedStartDate != null || filters.confirmedEndDate != null;
  if (hasDates && filters.timezoneOffset) {
    params.set('timezone', filters.timezoneOffset);
  }

  if (filters.northWest.trim()) {
    params.set('northwest', filters.northWest.trim());
  }
  if (filters.southEast.trim()) {
    params.set('southeast', filters.southEast.trim());
  }

  if (filters.geoPolygon && filters.geoPolygon.length > 0) {
    params.set('geopolygon', JSON.stringify(filters.geoPolygon));
  }

  if (filters.bandName.length > 0) {
    params.set('band_name', filters.bandName.join(','));
  }

  if (includeAuthOnly) {
    if (filters.tagName.length > 0) {
      params.set('tag_name', filters.tagName.join(','));
    }
    if (filters.campaignName.length > 0) {
      params.set('campaign_name', filters.campaignName.join(','));
    }
  }

  return params;
}

export function hasWorkspaceFilters(searchParams: URLSearchParams): boolean {
  return WORKSPACE_FILTER_PARAM_KEYS.some((key) => searchParams.has(key));
}

export function clearWorkspaceFilterParams(
  searchParams: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  for (const key of WORKSPACE_FILTER_PARAM_KEYS) {
    next.delete(key);
  }
  return next;
}

/** Merge workspace filter params into existing search params (replacing filter keys). */
export function mergeWorkspaceFiltersIntoSearchParams(
  existing: URLSearchParams,
  filters: WorkspaceAppliedFilters,
  options?: { includeAuthOnlyFilters?: boolean },
): URLSearchParams {
  const cleared = clearWorkspaceFilterParams(existing);
  const built = buildWorkspaceFilterSearchParams(filters, options);
  built.forEach((value, key) => {
    cleared.set(key, value);
  });
  return cleared;
}
